/**
 * @file Expert dashboard UI — pages/Dashboard/Expert/components/ExpertChartSlotBanner.tsx.
 */

import React, { useState } from "react";
import type { CementBoxPlotSlotSelection } from "../../../../components/DataVisualisation/CementBoxPlotChart";
import type { ChartSlotCodeEntry, ChartSlotCodeGroups } from "../../../../services/expertDatasetService";
import "../AllDataset/ExpertAllDataset.css";

interface Props {
  chartSlotSelection: CementBoxPlotSlotSelection;
  visibleCount: number;
  visibleSubmissionCodes: string[];
  onClear: () => void;
  codeGroups?: ChartSlotCodeGroups | null;
  selectedSubmissionId?: string | null;
  onSelectSubmissionId?: (submissionId: string | null) => void;
}

const SubmissionCodeChips: React.FC<{
  entries: ChartSlotCodeEntry[];
  selectedSubmissionId?: string | null;
  onSelectSubmissionId?: (submissionId: string | null) => void;
}> = ({ entries, selectedSubmissionId, onSelectSubmissionId }) => {
  if (entries.length === 0) {
    return <span className="material-overview-chart-slot-banner__codes-empty">—</span>;
  }

  return (
    <div className="material-overview-chart-slot-banner__code-list">
      {entries.map((entry) => {
        const isActive = selectedSubmissionId === entry.id;
        return (
          <button
            key={entry.id}
            type="button"
            className={`material-overview-chart-slot-banner__code-chip${
              isActive ? " material-overview-chart-slot-banner__code-chip--active" : ""
            }`}
            aria-pressed={isActive}
            title={entry.code}
            onClick={() =>
              onSelectSubmissionId?.(isActive ? null : entry.id)
            }
          >
            {entry.code}
          </button>
        );
      })}
    </div>
  );
};

const CollapsibleDatasetSection: React.FC<{
  title: string;
  count: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}> = ({ title, count, defaultExpanded = true, children }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="material-overview-chart-slot-banner__section">
      <button
        type="button"
        className="material-overview-chart-slot-banner__section-toggle"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <span className="material-overview-chart-slot-banner__section-toggle-left">
          <span className="material-overview-chart-slot-banner__chevron" aria-hidden>
            {isExpanded ? "▼" : "▶"}
          </span>
          <span className="material-overview-chart-slot-banner__codes-label">{title}</span>
        </span>
        <span className="material-overview-chart-slot-banner__section-count">{count}</span>
      </button>
      {isExpanded ? (
        <div className="material-overview-chart-slot-banner__section-body">{children}</div>
      ) : null}
    </div>
  );
};

const ExpertChartSlotBanner: React.FC<Props> = ({
  chartSlotSelection,
  visibleCount,
  visibleSubmissionCodes,
  onClear,
  codeGroups,
  selectedSubmissionId,
  onSelectSubmissionId,
}) => {
  const visibleCodeSet = new Set(visibleSubmissionCodes);
  const reviewCodeSet = new Set(
    (codeGroups?.reviewQueueEntries ?? []).map((entry) => entry.code)
  );
  const showNarrowedNote =
    visibleCount > 0 &&
    reviewCodeSet.size > 0 &&
    (visibleCodeSet.size < reviewCodeSet.size ||
      Array.from(reviewCodeSet).some((code) => !visibleCodeSet.has(code)));

  return (
    <div className="expert-review-panel material-overview-chart-slot-banner" role="status">
      <div className="material-overview-chart-slot-banner__body">
        <div className="material-overview-chart-slot-banner__text">
          <strong>List filtered by chart</strong>
          <span>
            {" "}
            Showing {visibleCount} submission
            {visibleCount === 1 ? "" : "s"}
            {chartSlotSelection.metric === "carbon"
              ? " (embodied carbon)"
              : " (embodied energy)"}
            {" · "}
            {chartSlotSelection.timeline}
            {chartSlotSelection.material ? ` · ${chartSlotSelection.material}` : ""}
            {chartSlotSelection.process !== "All" ? ` · ${chartSlotSelection.process}` : ""}. Search
            filters still apply.
          </span>
        </div>
        {codeGroups ? (
          <div className="material-overview-chart-slot-banner__sections">
            <CollapsibleDatasetSection
              title="All datasets"
              count={codeGroups.allEntries.length}
              defaultExpanded
            >
              <SubmissionCodeChips
                entries={codeGroups.allEntries}
                selectedSubmissionId={selectedSubmissionId}
                onSelectSubmissionId={onSelectSubmissionId}
              />
            </CollapsibleDatasetSection>
            <CollapsibleDatasetSection
              title="Flagged dataset"
              count={codeGroups.reviewQueueEntries.length}
              defaultExpanded
            >
              <SubmissionCodeChips
                entries={codeGroups.reviewQueueEntries}
                selectedSubmissionId={selectedSubmissionId}
                onSelectSubmissionId={onSelectSubmissionId}
              />
              {showNarrowedNote ? (
                <div className="material-overview-chart-slot-banner__filtered-note">
                  <span className="material-overview-chart-slot-banner__filtered-note-label">
                    After search filters
                  </span>
                  <span className="material-overview-chart-slot-banner__filtered-note-value">
                    {visibleSubmissionCodes.join(", ") || "—"}
                  </span>
                </div>
              ) : null}
            </CollapsibleDatasetSection>
          </div>
        ) : (
          <div className="material-overview-chart-slot-banner__codes">
            <span className="material-overview-chart-slot-banner__codes-label">
              Submission codes
            </span>
            <span className="material-overview-chart-slot-banner__codes-value">
              {chartSlotSelection.submissionCodes.length > 0
                ? chartSlotSelection.submissionCodes.join(", ")
                : "—"}
            </span>
          </div>
        )}
      </div>
      <button
        type="button"
        className="material-overview-chart-slot-banner__clear"
        onClick={onClear}
      >
        Clear chart filter
      </button>
    </div>
  );
};

/** React component — Expert Chart Slot Banner. */
export default ExpertChartSlotBanner;
